import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import Button, { ButtonProps } from '@mui/material/Button';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { useContext } from 'react';
import { AuthContext } from 'index';
import TextField from '@mui/material/TextField';
import { colors } from 'styles/colors';
import Link from 'atoms/Link';
import useMediaQuery from '@mui/material/useMediaQuery';

type SideBarProps = {
    isStateBarShown: boolean;
    setState: Function;
}

const SideBar = ({ isStateBarShown, setState }: SideBarProps) => {
    const auth = useContext(AuthContext);
    const user = useSelector((state: RootState) => state.user.user);
    const version = process.env.REACT_APP_VERSION;
    const matches = useMediaQuery('(min-width:580px)');
    
    const toggleDrawer = (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) 
        {
            return;
        }
        setState(!isStateBarShown);
    };
    const ColorButton = styled(Button)<ButtonProps>(() => ({
        color: '#000',
        border: '1px solid #000',
        backgroundColor: 'transparent',
        width: '6rem',
        height: '2.31rem',
        fontSize: '14px',
        fontWeight: '400',
        fontFamily: 'Figtree',
        padding: '5px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
        },
        borderRadius: '0.5rem',
    }));

    const onLogout = React.useCallback(() => {
        localStorage.removeItem('id_token');
        if (process.env.NODE_ENV !== 'development'){
            localStorage.setItem('logoutCheck', Date.now().toString());
        }
        auth.logout({ redirectUri: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://planetdiet.dk/' });
    }, [auth]);
            
    const list = () => (
        <Box
            role="presentation"
            onClick={toggleDrawer}
            onKeyDown={toggleDrawer}
        >
            {
                matches === false &&
                <div className={styles.base}>
                    <div className={styles.containerMenu}>
                        <div>
                            <p><Link underline='hover' text='Tidslinje' link='/time-line' linkCustomStyle={styles.linkCustomStyle}/></p>
                            <p><Link underline='hover' text='Spørgeskemaer' link='/questionnaries-list' linkCustomStyle={styles.linkCustomStyle}/></p>
                            <p><Link underline='hover' text='Kostdagbog' link='/fooddiary' linkCustomStyle={styles.linkCustomStyle}/></p>
                        </div>
                    </div>
                </div>
            }
            <div className={styles.base}>
                <div className={styles.containerMenu}>
                    <div className={styles.line}>
                    </div>
                </div>
            </div>
            <div className={styles.base}>
                <div className={styles.container}>
                    <div className='text'>Min profil</div>
                    <div className={styles.button}><ColorButton variant="contained" onClick={onLogout}>Log ud</ColorButton></div>
                </div>
            </div>
            <div className={styles.base}>
                <div className={styles.containerInput}>
                    <TextField id="outlined-basic" label='Navn' variant="outlined" value={user?.name} onClick={(event) => {event.stopPropagation();}} />
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerFirstLine}>© Copyright Projekt PLANETDIET {new Date().getFullYear()}</div>
                <div className={styles.footerLastLine}>App version {version}</div>
            </div>
        </Box>
    );

    return (
        <div>
            <Drawer
                anchor="right"
                open={isStateBarShown}
                onClose={toggleDrawer}
                sx={styles.contentSideBar}
            >
                {list()}
            </Drawer>
        </div>
    );
};

const styles = {
    contentSideBar: {
        '& .MuiPaper-root': {
            marginTop: '4.5rem',
            width: '24.9375rem',
            height: '93%',

            '@media (max-width: 425px)': {
                width: '100%',
            },
        },
        '& .MuiBox-root': {
            width: '100%',
        },
    },
    base: css({
        display: 'flex',
        justifyContent: 'center',
        '& .MuiInputBase-root': {
            display: 'flex',
            width: '20rem',
            height: '3.5rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: '0',

            '@media (max-width: 425px)': {
                width: '18rem',
            },
        },
    }),
    containerMenu: css({
        display: 'flex',
        justifyContent: 'space-between',
        width: '20rem',
        '> .text': {
            color: '#1E1E1E',
            fontFamily: 'Figtree',
            fontSize: '36px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '3.375rem',
            letterSpacing: '-0.02475rem',
        },

        '@media (max-width: 425px)': {
            width: '18rem',
        },
    }),
    line: css({
        borderBottom: '1px solid rgba(119, 134, 252, 0.33)',
        width: '100%',
    }),
    container: css({
        display: 'flex',
        justifyContent: 'space-between',
        width: '20rem',
        marginTop: '3.31rem',
        '> .text': {
            color: '#1E1E1E',
            fontFamily: 'Figtree',
            fontSize: '36px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '3.375rem',
            letterSpacing: '-0.02475rem',
        },

        '@media (max-width: 580px)': {
            marginTop: '1rem',
        },

        '@media (max-width: 425px)': {
            width: '18rem',
        },
    }),
    containerInput: css({
        display: 'flex',
        justifyContent: 'space-between',
        width: '20rem',
        marginTop: '2.06rem',
        '& .MuiOutlinedInput-input': {
            fontFamily: 'Figtree !important',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal',
        },
        '@media (max-width: 425px)': {
            width: '18rem',
        },
    }),
    button: css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Figtree',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
    }),
    footer: css({
        position: 'absolute',
        bottom: '30px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '3.5rem',
        justifyContent: 'center',
    }),
    footerFirstLine: css({
        color: 'rgba(0, 0, 0, 0.48)',
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '24px',
        letterSpacing: '-0.01444rem',
    }),
    footerLastLine: css({
        color: 'rgba(0, 0, 0, 0.48)',
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '24px',
        letterSpacing: '-0.01306rem',
    }),

    linkButtonsContainer: css({
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '1.5rem',
        '@media (max-width: 427px)': {
            paddingLeft: '0.5rem',
        },
    }),
    linkCustomStyle: css({
        marginRight: '1.31rem !important',
        fontFamily: 'Figtree !important',
        fontSize:'14px !important',
        fontStyle: 'normal !important',
        fontWeight: '700 !important',
        lineHeight: 'normal !important',
        color: colors.primaryText + ' !important',
        '@media (max-width: 427px)': {
            marginRight: '1.31rem !important',
        },
    }),
};

export default SideBar;
